"use client";

import { useEffect, useState } from "react";
import { db } from "@/firebase/config";
import { doc, getDoc, serverTimestamp, setDoc } from "@/lib/firestore-instrumented";
import { isAndroidNativeApp, isNativeApp } from "@/lib/nativePlatform";
import { routeNativePath } from "@/lib/nativeRouting";

const CHANNEL_ID = "sproutly_activity";

async function getNativeMetadata() {
  const metadata = {
    appId: "space.sproutly.android",
    packageName: "space.sproutly.android",
  };

  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    metadata.appId = info?.id || metadata.appId;
    metadata.packageName = info?.id || metadata.packageName;
    metadata.appVersion = info?.version || "";
    metadata.buildNumber = info?.build || "";
  } catch (err) {
    console.debug("[native-push] app metadata unavailable", err);
  }

  try {
    const { Device } = await import("@capacitor/device");
    const [deviceInfo, deviceId] = await Promise.all([
      Device.getInfo(),
      Device.getId().catch(() => null),
    ]);
    metadata.deviceModel = deviceInfo?.model || "";
    metadata.osVersion = deviceInfo?.osVersion || "";
    metadata.nativeInstallId = deviceId?.identifier || "";
    metadata.isVirtual = Boolean(deviceInfo?.isVirtual);
    metadata.webViewVersion = deviceInfo?.webViewVersion || "";
  } catch (err) {
    console.debug("[native-push] device metadata unavailable", err);
  }

  return metadata;
}

async function createAndroidChannel(PushNotifications) {
  if (!isAndroidNativeApp()) return;
  try {
    await PushNotifications.createChannel({
      id: CHANNEL_ID,
      name: "Sproutly activity",
      description: "Replies, mentions, and space activity",
      importance: 4,
      visibility: 1,
      lights: true,
      lightColor: "#719775",
      vibration: true,
    });
  } catch (err) {
    console.debug("[native-push] channel setup failed", err);
  }
}

function extractNotificationPath(action) {
  const data = action?.notification?.data || action?.data || {};
  return data.path || data.url || data.link || action?.notification?.link || "";
}

export function useNativePushRegistration({ user } = {}) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setReady(false);
    setError(null);

    if (!user?.uid || !isNativeApp()) return;

    let cancelled = false;
    const handles = [];

    (async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        await createAndroidChannel(PushNotifications);

        handles.push(
          await PushNotifications.addListener("registration", async ({ value: token }) => {
            if (!token || cancelled) return;
            const metadata = await getNativeMetadata();
            if (cancelled) return;

            await setDoc(doc(db, "userPush", user.uid), { updatedAt: serverTimestamp() }, { merge: true });
            const tokenRef = doc(db, `userPush/${user.uid}/nativeTokens/${token}`);
            const tokenSnap = await getDoc(tokenRef);
            if (cancelled) return;
            await setDoc(
              tokenRef,
              {
                token,
                platform: "android",
                provider: "fcm",
                channelId: CHANNEL_ID,
                firebaseProjectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "",
                firebaseMessagingSenderId:
                  process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "",
                firebaseAuthDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || "",
                ...metadata,
                ...(tokenSnap.exists() ? {} : { createdAt: serverTimestamp() }),
                lastSeenAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
            if (!cancelled) setReady(true);
          })
        );

        handles.push(
          await PushNotifications.addListener("registrationError", (err) => {
            if (cancelled) return;
            setError(err);
            console.warn("[native-push] registration failed", err);
          })
        );

        handles.push(
          await PushNotifications.addListener("pushNotificationReceived", (notification) => {
            window.dispatchEvent(
              new CustomEvent("sproutly-native-push-received", {
                detail: notification,
              })
            );
          })
        );

        handles.push(
          await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            routeNativePath(extractNotificationPath(action));
          })
        );

        const current = await PushNotifications.checkPermissions();
        const permission =
          current?.receive === "granted" ? current : await PushNotifications.requestPermissions();
        if (permission?.receive !== "granted" || cancelled) return;

        await PushNotifications.register();
      } catch (err) {
        if (!cancelled) {
          setError(err);
          console.warn("[native-push] setup failed", err);
        }
      }
    })();

    return () => {
      cancelled = true;
      handles.forEach((handle) => {
        try {
          handle?.remove?.();
        } catch {}
      });
    };
  }, [user?.uid]);

  return { ready, error };
}
